import AbstractGlobeXS from '@assets/abstract-globe-xs.png';
import { LightTooltip } from '@components/display/tooltips';
import { useGetCountryFlag } from '@event/hooks/useGetCountryFlag';
import { useGetLanguageSpokenOptionsQuery } from '@event/services/events';
import { EventTypes, IEventListCardProps, RegistrationLabel, RegistrationStatus } from '@event/types/event';
import { useAppSelector } from '@hooks/generic';
import GenericBadgeIcon from '@levelSystem/assets/generic-neutral-badge.svg?react';
import LanguageIcon from '@mui/icons-material/Language';
import { Avatar, Button, CircularProgress, Typography } from '@mui/material';
import { getDateFnsLocale } from '@utils/common';
import { getEventTypeShortLabel, getMarketLabel, getRegistrationLabel } from '@event/utils';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { LanguageLabel } from '@components/utility/labels/LanguageLabel/LanguageLabel';

const EventListCard = ({ event, markets, user, ...props }: IEventListCardProps) => {
    const intl = useIntl();
    const currentLocale = useAppSelector((state) => state.translation.locale);
    const dateLocale = getDateFnsLocale(currentLocale);
    const flagUrl = useGetCountryFlag(event);
    const isOnSite = useMemo(
        () => event?.type && [EventTypes.PRIVATE_ONSITE, EventTypes.PUBLIC_ONSITE].includes(event.type),
        [event?.type]
    );

    const { data: languageOptions, isLoading: areLanguageOptionsLoading } = useGetLanguageSpokenOptionsQuery(
        undefined,
        {
            selectFromResult: (response) => ({
                ...response,
                data: [
                    {
                        label: 'None',
                        value: '',
                    },
                    ...(response.data?.map((language) => ({ label: language.name, value: language.code })) ?? []),
                ],
            }),
        }
    );

    const eventHasRequiredBadges = !!event?.level_badge_requirements.length;

    const registrationStatus: RegistrationLabel | null = useMemo(() => {
        if (!user || !event) return null;

        const isEventPublished = event.published;

        const isUserAccepted = event.subscription_status === RegistrationStatus.ACCEPTED;

        const hasMaxCapacityReached = event.number_attendees >= event.max_capacity;

        const userRequiresBadges =
            !event.user_meets_level_badge_requirements?.meets_requirements &&
            !event.user_meets_level_badge_requirements?.meets_requirements_without_temp_badges;

        if (!isEventPublished) {
            return RegistrationLabel.UNPUBLISHED;
        }

        if (isUserAccepted) {
            return RegistrationLabel.REGISTERED;
        }

        if (hasMaxCapacityReached) {
            return RegistrationLabel.CLOSED;
        }

        if (userRequiresBadges) {
            return RegistrationLabel.BADGES_REQUIRED;
        }

        return null;
    }, [event, user]);

    return (
        <Link
            className="py-2.5 sm:py-8 px-3.5 sm:px-12 shadow cursor-pointer hover:shadow-lg"
            to={`/app/events/${event?.type}/${event?.id}`}
            {...props}
        >
            <div className="h-full flex flex-col sm:flex-row gap-3 sm:gap-7">
                <div className="flex items-center sm:flex-col sm:justify-between gap-5 sm:gap-0">
                    <div className="bg-azureish-white rounded-full w-[38px] h-[38px] font-semibold text-xs text-spanish-blue flex justify-center">
                        <div className="flex flex-col justify-center items-center text-center w-[20px] leading-3 font-inter">
                            <p>
                                {event?.date_start
                                    ? format(event?.date_start ?? '', 'd MMM', { locale: dateLocale })
                                    : ''}
                            </p>
                        </div>
                    </div>
                    <img src={isOnSite ? flagUrl : AbstractGlobeXS} className="w-[42px] h-[28px] rounded-sm" />
                </div>
                <div className="text-base text-auro-metal-saurus h-full sm:h-auto">
                    <p className="line-clamp-1 text-sm md:text-base">
                        {event?.date_start
                            ? format(event?.date_start ?? '', 'EEEE, MMM do yyyy, hh:mm aa O', { locale: dateLocale })
                            : ''}
                    </p>
                    <p className="font-bold text-raisin-black line-clamp-2">{event?.name}</p>
                    {event?.type && [EventTypes.PRIVATE_ONSITE, EventTypes.PUBLIC_ONSITE].includes(event.type) ? (
                        <p className="line-clamp-1">
                            {event.country_name}
                            {event.city ? `, ${event.city}` : ''}
                            {event.custom_location || event?.location
                                ? `, ${event?.custom_location || event?.location}`
                                : ''}
                        </p>
                    ) : null}
                    <div className="flex flex-col gap-2 mt-1 sm:items-center sm:gap-5 sm:flex-row">
                        {event?.language_spoken ? (
                            <div className="flex items-center gap-1">
                                {areLanguageOptionsLoading ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <LanguageLabel
                                        langCode={
                                            event?.language_spoken.toString().toLowerCase() === 'en'
                                                ? 'us_uk'
                                                : event?.language_spoken.toString().toLowerCase()
                                        }
                                        langLabel={
                                            languageOptions?.find((lo) => lo.value === event?.language_spoken)?.label ??
                                            null
                                        }
                                    />
                                )}
                            </div>
                        ) : null}
                        <div className="flex items-center gap-1">
                            <LanguageIcon color="neutral-800" className="w-[18px] h-[18px]" />
                            <Typography fontSize={16} color="neutral-800.main" className="line-clamp-1">
                                {event?.type ? getEventTypeShortLabel(event.type) : ''}
                            </Typography>
                        </div>
                        {eventHasRequiredBadges && (
                            <div className="flex items-center gap-1">
                                <GenericBadgeIcon />
                                <Typography fontSize={16} color="neutral-800.main" className="line-clamp-1">
                                    <FormattedMessage
                                        id="events.labels.badgeRequired"
                                        defaultMessage="Badge Required"
                                        description="/app/events/{id} - Label indicating event requires a badge"
                                    />
                                </Typography>
                            </div>
                        )}
                    </div>
                </div>
                <div className="sm:ml-auto sm:my-auto flex flex-col sm:items-center gap-2.5 min-w-[135px]">
                    {event?.markets.length ? (
                        <LightTooltip
                            title={
                                <div className="flex flex-col items-center font-inter font-medium text-xs text-spanish-blue gap-1 py-1.5 px-3.5">
                                    {event?.markets.map((market) => {
                                        const marketInfo = markets?.find((x) => x.id === market);
                                        const marketLabel = marketInfo?.title
                                            ? getMarketLabel(marketInfo.title, intl)
                                            : marketInfo?.title || market;
                                        return <p key={`market-info-${market}`}>{marketLabel}</p>;
                                    })}
                                </div>
                            }
                            placement="top"
                            arrow
                        >
                            <div className="flex items-center font-inter font-medium text-xs text-spanish-blue gap-1">
                                <span>
                                    <FormattedMessage
                                        id="events.labels.relatedMarkets"
                                        defaultMessage="Related Markets"
                                        description="/app/events/{id} - Label for markets related to the event"
                                    />
                                </span>
                                <div className="flex items-center justify-center text-white bg-spanish-blue rounded-full w-[18px] h-[18px]">
                                    <span>{event?.markets.length}</span>
                                </div>
                            </div>
                        </LightTooltip>
                    ) : null}
                    <Button
                        sx={{
                            border: '1px solid #DFE4EA',
                            borderRadius: '50px',
                        }}
                        color="secondary-alt"
                        size="large"
                        variant="outlined"
                    >
                        <span className="font-medium text-base text-auro-metal-saurus">
                            <FormattedMessage
                                id="common.actions.learnMore"
                                defaultMessage="Learn more"
                                description="Common button text to view more details"
                            />
                        </span>
                    </Button>
                    {registrationStatus ? (
                        <div
                            className={clsx(
                                'w-full sm:w-fit py-1.5 px-2.5 rounded text-white font-medium text-xs text-center',
                                {
                                    'bg-caribbean-green': registrationStatus === RegistrationLabel.REGISTERED,
                                    'bg-gray51': registrationStatus === RegistrationLabel.CLOSED,
                                    'bg-error-500': registrationStatus === RegistrationLabel.UNPUBLISHED,
                                    'bg-primary-600': registrationStatus === RegistrationLabel.BADGES_REQUIRED,
                                }
                            )}
                        >
                            {getRegistrationLabel(registrationStatus, intl)}
                        </div>
                    ) : null}
                </div>
            </div>
        </Link>
    );
};

export { EventListCard };
